import { FetchResult } from '@apollo/client'
import { css } from '@emotion/react'
import { Button, Cell, Grid, HFlow, Theme, Tooltip, useTheme } from 'bold-ui'
import {
  CpfField,
  Form,
  FormRenderProps,
  MunicipioSelectField,
  MunicipioSelectModel,
  SubmitButton,
  TextField,
} from 'components/form'
import { CnpjField } from 'components/form/field/CnpjField'
import { TipoPessoaGroupField } from 'components/form/field/TipoPessoaRadioGroupField'
import { FormApi } from 'final-form'
import { useSalvarCredencialIntegracaoMutation } from 'graphql/hooks.generated'
import { RecursoIntegracaoEnum, SalvarCredencialIntegracaoMutation, TipoPessoaEnum } from 'graphql/types.generated'
import { useState } from 'react'
import { isHttpsConnection } from 'util/https'
import { metaPath } from 'util/metaPath'
import {
  cnpj,
  cpf,
  createValidator,
  email,
  maxLength,
  minLength,
  nome,
  required,
  urlHttps,
  validate,
} from 'util/validation'

import { CredencialIntegracaoGeradaModal, CredencialIntegracaoUserPassword } from './CredencialIntegracaoGeradaModal'
import RecursosCredencialIntegracaoGroupBoxField from './RecursosCredencialIntegracaoGroupBoxField'

interface CredencialIntegracaoFormModel {
  nomeResponsavel: string
  tipoPessoa: TipoPessoaEnum
  cpfCnpj: string
  email: string
  nomeCredencial: string
  recursos: RecursoIntegracaoEnum[]
  urlWebhookRegulacao: string
  municipiosVinculados: MunicipioSelectModel[]
}

const path = metaPath<CredencialIntegracaoFormModel>()

const validator = createValidator<CredencialIntegracaoFormModel>(
  {
    nomeCredencial: [required, minLength(3), maxLength(100)],
    nomeResponsavel: [required, minLength(5), maxLength(100), nome()],
    tipoPessoa: [required],
    cpfCnpj: [required],
    email: [required, email, minLength(6), maxLength(100)],
    urlWebhookRegulacao: [maxLength(255), urlHttps],
    recursos: [required],
  },
  (values, errors) => {
    if (values.tipoPessoa === TipoPessoaEnum.FISICA) {
      errors.cpfCnpj = errors.cpfCnpj ?? validate(values.cpfCnpj, cpf)
    }
    if (values.tipoPessoa === TipoPessoaEnum.JURIDICA) {
      errors.cpfCnpj = errors.cpfCnpj ?? validate(values.cpfCnpj, cnpj)
    }
    if (values.recursos?.includes(RecursoIntegracaoEnum.REGULACAO)) {
      errors.urlWebhookRegulacao = errors.urlWebhookRegulacao ?? validate(values.urlWebhookRegulacao, required)
    }

    return errors
  }
)

interface CredenciaisIntegracaoFormProps {
  readonly refetch: () => void
}

export function CredenciaisIntegracaoForm({ refetch }: CredenciaisIntegracaoFormProps) {
  const [save] = useSalvarCredencialIntegracaoMutation()
  const theme = useTheme()
  const styles = createStyles(theme)

  const [isModelOpen, setIsModelOpen] = useState(false)
  const [userPassword, setUserPassword] = useState<CredencialIntegracaoUserPassword>()

  const onSubmitSuccess = (
    payload: FetchResult<SalvarCredencialIntegracaoMutation>,
    form: FormApi<CredencialIntegracaoFormModel>
  ) => {
    refetch()
    setUserPassword(payload.data.salvarCredencialIntegracao.userPassword)
    setIsModelOpen(true)
    setTimeout(() => {
      form.reset()
      form.getRegisteredFields().forEach((field) => form.resetFieldState(field))
    })
  }

  const handleSubmit = (values: CredencialIntegracaoFormModel, form: FormApi<CredencialIntegracaoFormModel>) => {
    if (!isHttpsConnection) {
      return
    }

    const hasRegulacao = values.recursos?.includes(RecursoIntegracaoEnum.REGULACAO)
    const computedValue = {
      ...values,
      municipiosVinculados: hasRegulacao ? values.municipiosVinculados?.map((municipio) => municipio.id) : undefined,
      urlWebhookRegulacao: hasRegulacao ? values.urlWebhookRegulacao : undefined,
    }

    return save({
      variables: { input: computedValue },
    }).then((payload) => onSubmitSuccess(payload, form))
  }

  const handleCloseIntegracaoGeradaModal = () => {
    setIsModelOpen(false)
    setUserPassword(null)
  }

  const renderForm = (renderProps: FormRenderProps<Partial<CredencialIntegracaoFormModel>>) => {
    const isPessoaJuridica = renderProps.values.tipoPessoa === TipoPessoaEnum.JURIDICA

    const onReset = () => {
      renderProps.form.reset()
      renderProps.form.getRegisteredFields().forEach((field) => renderProps.form.resetFieldState(field))
    }

    const hasRegulacao = renderProps.values.recursos?.includes(RecursoIntegracaoEnum.REGULACAO)

    return (
      <form noValidate>
        <Grid>
          <Cell size={5} alignSelf='flex-start'>
            <TextField name={path.nomeCredencial} label='Nome da credencial' required />
          </Cell>
          <Cell size={12}>
            <RecursosCredencialIntegracaoGroupBoxField
              name={path.recursos}
              label='Tipo de credencial'
              required
              lines={2}
            />
          </Cell>
          {hasRegulacao && (
            <Cell size={12} alignSelf='flex-start'>
              <Grid css={styles.regulacaoBox}>
                <Cell size={12} alignSelf='flex-start'>
                  <TextField name={path.urlWebhookRegulacao} label='URL do webhook' required />
                </Cell>
                <Cell size={5} alignSelf='flex-start'>
                  <MunicipioSelectField name={path.municipiosVinculados} label='Vincular municípios' multiple ativo />
                </Cell>
              </Grid>
            </Cell>
          )}
          <Cell size={12}>
            <TipoPessoaGroupField name={path.tipoPessoa} required label='Gerar credencial para' />
          </Cell>
          <Cell size={5}>
            <TextField name={path.nomeResponsavel} label='Responsável' required />
          </Cell>
          <Cell size={3}>
            {isPessoaJuridica ? (
              <CnpjField name={path.cpfCnpj} label='CNPJ' required />
            ) : (
              <CpfField name={path.cpfCnpj} label='CPF' required />
            )}
          </Cell>
          <Cell size={4} />
          <Cell size={5}>
            <TextField name={path.email} label='E-mail' required />
          </Cell>
          <Cell size={12}>
            <HFlow justifyContent='flex-end'>
              <Button size='small' onClick={onReset}>
                Limpar campos
              </Button>
              <Tooltip
                text={
                  !isHttpsConnection && 'Para gerar credencias é necessário que a instalação possua HTTPS configurado.'
                }
              >
                <SubmitButton size='small' handleSubmit={renderProps.handleSubmit} disabled={!isHttpsConnection}>
                  Gerar credencial
                </SubmitButton>
              </Tooltip>
            </HFlow>
          </Cell>
        </Grid>
      </form>
    )
  }

  return (
    <div css={styles.box}>
      <CredencialIntegracaoGeradaModal
        userPassword={userPassword}
        isOpen={isModelOpen}
        onClose={handleCloseIntegracaoGeradaModal}
      />
      <Form render={renderForm} validate={validator} onSubmit={handleSubmit} />
    </div>
  )
}

const createStyles = (theme: Theme) => ({
  box: css`
    padding: 1rem;
    border: 1px solid ${theme.pallete.divider};
  `,
  regulacaoBox: css`
    padding: 0.5rem;
    border: 1px solid ${theme.pallete.divider};
  `,
})
